import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

declare global {
  interface Window {
    plausible: (event: string, options?: { props?: Record<string, string | number | boolean> }) => void;
  }
}

const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname.includes('webcontainer');

// Track page views with error handling and debugging
export const trackPageView = (url: string) => {
  try {
    console.debug('[Analytics] Tracking pageview:', url);
    if (window.plausible) {
      // Use the standard pageview event without props for better compatibility
      window.plausible('pageview');
      console.debug('[Analytics] Pageview tracked successfully');
    } else {
      console.warn('[Analytics] Plausible not initialized');
    }
  } catch (error) {
    console.error('[Analytics] Error tracking pageview:', error);
  }
};

// Track custom events with error handling and debugging
export const trackEvent = (eventName: string, props?: Record<string, string | number | boolean>) => {
  try {
    console.debug('[Analytics] Tracking event:', eventName, props);
    if (window.plausible) {
      // Add event prefix for better organization in Plausible dashboard
      const prefixedEvent = `${eventName}`;
      window.plausible(prefixedEvent, { 
        props: {
          ...props,
          path: window.location.pathname,
        }
      });
      console.debug('[Analytics] Event tracked successfully');
    } else {
      console.warn('[Analytics] Plausible not initialized');
    }
  } catch (error) {
    console.error('[Analytics] Error tracking event:', error);
  }
};

// Track outbound links with data attributes
export const trackOutboundLink = (url: string, text?: string) => {
  try {
    console.debug('[Analytics] Tracking outbound link:', url);
    trackEvent('Outbound Link: Click', { 
      url,
      text: text || '',
    });
  } catch (error) {
    console.error('[Analytics] Error tracking outbound link:', error);
  }
};

// Track CTA clicks
export const trackCTAClick = (ctaText: string, ctaLocation: string) => {
  try {
    console.debug('[Analytics] Tracking CTA click:', ctaText, ctaLocation);
    trackEvent('CTA: Click', { 
      text: ctaText,
      location: ctaLocation,
    });
  } catch (error) {
    console.error('[Analytics] Error tracking CTA click:', error);
  }
};

// Track navigation clicks
export const trackNavigationClick = (linkText: string, linkPath: string) => {
  try {
    console.debug('[Analytics] Tracking navigation click:', linkText, linkPath);
    trackEvent('Navigation: Click', {
      text: linkText,
      path: linkPath,
    });
  } catch (error) {
    console.error('[Analytics] Error tracking navigation click:', error);
  }
};

// Track form interactions
export const trackFormInteraction = (formType: string, status: 'error' | 'success', details?: string) => {
  try {
    console.debug('[Analytics] Tracking form interaction:', formType, status);
    trackEvent(`Form: ${status === 'error' ? 'Error' : 'Success'}`, {
      type: formType,
      details,
    });
  } catch (error) {
    console.error('[Analytics] Error tracking form interaction:', error);
  }
};

// Track tier selection
export const trackTierSelection = (tierName: string, tierLevel: number, spots: number) => {
  try {
    console.debug('[Analytics] Tracking tier selection:', tierName);
    trackEvent('Tier: View', {
      name: tierName,
      level: tierLevel,
      spots,
    });
  } catch (error) {
    console.error('[Analytics] Error tracking tier selection:', error);
  }
};

// Custom hook for tracking page views and scroll depth
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    try {
      console.debug('[Analytics] Setting up page tracking for:', location.pathname);
      trackPageView(location.pathname);

      // Track scroll depth
      let maxScroll = 0;
      const handleScroll = () => {
        try {
          const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
          if (scrollableHeight <= 0) return;
          const scrollPercent = Math.round(
            (window.scrollY / scrollableHeight) * 100
          );

          if (scrollPercent > maxScroll && scrollPercent % 25 === 0) {
            maxScroll = scrollPercent;
            console.debug('[Analytics] Tracking scroll depth:', scrollPercent);
            trackEvent('Scroll: Depth', { 
              depth: scrollPercent,
              page: location.pathname,
            });
          }
        } catch (error) {
          console.error('[Analytics] Error tracking scroll depth:', error);
        }
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    } catch (error) {
      console.error('[Analytics] Error setting up page tracking:', error);
    }
  }, [location]);

  // Track outbound links
  useEffect(() => {
    try {
      console.debug('[Analytics] Setting up outbound link tracking');
      const handleClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const link = target.closest('a');
        
        if (link?.href && link.host !== window.location.host) {
          trackOutboundLink(link.href, link.textContent);
        }
      };

      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    } catch (error) {
      console.error('[Analytics] Error setting up outbound link tracking:', error);
    }
  }, []);
};